import { useMemo, useState } from 'react';

import { BasicModal } from '../../../../../components/modal';
import { useWebSocketContext } from '../../../../../contexts';
import { useForm } from '../../../../../hooks';
import { IuseForm } from '../../../../../hooks/useForm';
import DashboardService, {
  ShiftParam,
} from '../../../../../services/DashboardService';
import { getUTCDateTime } from '../../../../../utils/date';
import {
  dashboardActions,
  shiftActions,
  useDashboardContext,
  useShifts,
} from '../../../contexts';
import {
  GRAPH_GENERATE_STATUS,
  SHIFT_MANAGE_STATUS,
  facilityActions,
  useFacilityContext,
} from '../../../contexts/FacilityProvider';
import { FacilitySnapShot } from '../../../contexts/FacilityProvider.types';
import { ShiftProviderState } from '../../../contexts/ShiftProvider.types';
import ShiftController from './ShiftController';
import { ShiftControllerModalProps } from './ShiftControllerModal.types';

export default function ShiftControllerModal({
  open,
  onClose = () => {},
  sx = {},
  ...rest
}: ShiftControllerModalProps) {
  const {
    state: { selectedFacility, facilitySnapshots },
    dispatch: facilityDispatch,
  } = useFacilityContext();
  const {
    state: {
      days: { data: weekDaysList, selected: selectedStartDay },
      scenarios: { selected: selectedScenario },
    },
    dispatch: dashboardDispatch,
  } = useDashboardContext();
  const {
    state: {
      shifts: { data: shiftData = {} },
    },
    dispatch: shiftDispatch,
  } = useShifts();
  const { sessionId } = useWebSocketContext();

  const [prevShiftControllerSelectedDay, setPrevShiftControllerSelectedDay] =
    useState(selectedStartDay);
  const [shiftControllerSelectedDay, setshiftControllerSelectedDay] =
    useState(selectedStartDay);
  if (prevShiftControllerSelectedDay !== selectedStartDay) {
    setPrevShiftControllerSelectedDay(selectedStartDay);
    setshiftControllerSelectedDay(selectedStartDay);
  }

  const onSubmit = async (v: ShiftProviderState['shifts']['data']) => {
    const changedShifts: ShiftParam[] =
      Object.values(v)
        ?.filter((d) => ['new', 'update', 'delete'].includes(d?.status || ''))
        ?.map((d) => {
          return {
            shift_id: Number(d.id),
            provider_count: d.providerCount,
            provider_type_name: d.providerType,
            shift_length: d.shiftLength,
            shift_start: getUTCDateTime(d.startDate),
            status: d?.status || 'update',
          };
        }) || [];

    try {
      if (sessionId && selectedScenario && changedShifts.length > 0) {
        const newShifts = { ...v };

        facilityDispatch(
          facilityActions.updateShiftSessionId(
            selectedFacility,
            SHIFT_MANAGE_STATUS.IN_PROGRESS
          )
        );
        await DashboardService.updateShift(
          {
            scenario: selectedScenario,
            user_id: sessionId,
            facility_code: selectedFacility,
          },
          changedShifts
        );
        facilityDispatch(
          facilityActions.updateShiftSessionId(
            selectedFacility,
            SHIFT_MANAGE_STATUS.SUCCESS,
            sessionId
          )
        );
        dashboardDispatch(
          dashboardActions.setSelectedDay(shiftControllerSelectedDay as string)
        );
        changedShifts.forEach((d) => {
          if (d.status === 'delete') {
            delete newShifts?.[d.shift_id];
          } else
            newShifts[d.shift_id] = {
              ...newShifts[d.shift_id],
              status: undefined,
            };
        });
        shiftDispatch(shiftActions.setShifts(newShifts));
      }
    } catch (error) {
      console.error('Api Error💥💥💥', error);
      facilityDispatch(
        facilityActions.updateShiftSessionId(
          selectedFacility,
          SHIFT_MANAGE_STATUS.ERROR
        )
      );
    }
    onClose?.();
  };
  const form = useForm({}, {}, onSubmit);
  const { isFormValid, formData, reset } = form;

  const activeFacilitySnapShot: FacilitySnapShot = {
    userGraphGenerationStatus: GRAPH_GENERATE_STATUS.IDLE_GENERATED_GRAPH,
    userShiftManageStatus: SHIFT_MANAGE_STATUS.IDLE,
    clientId: undefined,
    ...(facilitySnapshots?.[selectedFacility] || {}),
  };

  const [prevShiftData, setPrevShiftData] =
    useState<ShiftProviderState['shifts']['data']>();
  const patchShiftData = (data: ShiftProviderState['shifts']['data']) => {
    const newShiftData: IuseForm['formData'] = { ...data };
    Object.values(data).forEach((d) => {
      newShiftData[d.id] = { ...d, baseStartDate: d.startDate };
    });
    reset(newShiftData);
  };
  if (JSON.stringify(prevShiftData) !== JSON.stringify(shiftData)) {
    setPrevShiftData(shiftData);
    patchShiftData(shiftData);
  }
  const isShiftChanged = useMemo(
    () =>
      Object.values(formData).some((d) =>
        ['new', 'update', 'delete'].includes(d?.status || '')
      ),
    [formData]
  );
  const isSubmitDisabled =
    activeFacilitySnapShot?.userShiftManageStatus ===
      SHIFT_MANAGE_STATUS.IN_PROGRESS ||
    !isFormValid ||
    !isShiftChanged;
  const isUpdating =
    activeFacilitySnapShot?.userShiftManageStatus ===
    SHIFT_MANAGE_STATUS.IN_PROGRESS;

  const handleDaySelect = (v: string) => {
    setshiftControllerSelectedDay(v);
  };
  return (
    <BasicModal open={open} onClose={onClose} sx={{ ...sx }} {...rest}>
      <ShiftController
        form={form}
        isUpdating={isUpdating}
        isSubmitDisabled={isSubmitDisabled}
        onCancel={onClose}
        onDaySelect={handleDaySelect}
        weekDaysList={weekDaysList}
        selectedStartDay={shiftControllerSelectedDay}
      />
    </BasicModal>
  );
}

// const validationRule: FormValidationRules = {
//   '*': (value) => {
//     if (value?.shiftLength > APP_ENV.MAXIMUM_SHIFT_LENGTH_HR) {
//       return `Shift Length exceeds the maximum limit of ${APP_ENV.MAXIMUM_SHIFT_LENGTH_HR} hrs`;
//     }
//     return undefined;
//   },
// };
